import { useRef } from "react";
import { motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { ArrowDown, FileSearch, ShieldCheck } from "lucide-react";
import { ButtonGold } from "./Buttons";
import { Eyebrow, MaskedLine } from "./Reveal";
import { scrollToSection } from "../config";

const DOSSIER_ROWS = [
    { label: "Vazamento operacional", value: "CONFIRMADO", alert: true },
    { label: "Conversão de leads", value: "EM QUEDA" },
    { label: "Churn de clientes", value: "ACIMA DO ESPERADO" },
    { label: "Gargalo principal", value: "CEGUEIRA DE DADOS" },
];

export default function Hero({ onOpenForm }) {
    const heroRef = useRef(null);
    const mx = useMotionValue(0);
    const my = useMotionValue(0);
    const rotateX = useSpring(useTransform(my, [-0.5, 0.5], [5, -5]), { stiffness: 60, damping: 20 });
    const rotateY = useSpring(useTransform(mx, [-0.5, 0.5], [-5, 5]), { stiffness: 60, damping: 20 });

    const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
    const cardY = useTransform(scrollYProgress, [0, 1], [0, -70]);
    const glowY = useTransform(scrollYProgress, [0, 1], [0, 140]);
    const gridY = useTransform(scrollYProgress, [0, 1], [0, 60]);

    const onMouseMove = (e) => {
        if (!heroRef.current) return;
        const r = heroRef.current.getBoundingClientRect();
        mx.set((e.clientX - r.left) / r.width - 0.5);
        my.set((e.clientY - r.top) / r.height - 0.5);
    };

    return (
        <section
            id="topo"
            ref={heroRef}
            onMouseMove={onMouseMove}
            className="relative flex min-h-screen items-center overflow-hidden bg-ink-950 pb-24 pt-32"
            data-testid="hero-section"
        >
            {/* backdrop layers */}
            <motion.div style={{ y: gridY }} className="absolute inset-0 bg-grid-faint opacity-70" />
            <motion.div
                style={{ y: glowY }}
                className="absolute -top-44 left-[-12%] h-[620px] w-[620px] rounded-full bg-[radial-gradient(circle,rgba(201,162,39,0.10),transparent_65%)]"
            />
            <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-40 bg-gradient-to-b from-transparent to-[#0a0a0a]" />
            <p className="pointer-events-none absolute right-10 top-1/2 hidden rotate-90 font-mono text-[10px] uppercase tracking-[0.5em] text-[#4a4a4a] xl:block">
                Dossiê Executivo — Confidencial
            </p>

            <div className="relative z-10 mx-auto grid w-full max-w-7xl grid-cols-1 items-center gap-16 px-6 pb-8 lg:grid-cols-12 lg:gap-10 lg:px-10">
                <div className="lg:col-span-7">
                    <motion.div
                        initial={{ opacity: 0, y: 14 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.1 }}
                    >
                        <Eyebrow>MasterClaus — Investigação Comercial</Eyebrow>
                    </motion.div>

                    <h1 className="mt-9 font-serif text-[clamp(2.5rem,6.2vw,4.8rem)] font-medium leading-[1.07] text-white">
                        <MaskedLine delay={0.25}>O seu negócio está</MaskedLine>
                        <MaskedLine delay={0.38}>
                            <em className="italic text-gold gold-glow-text">perdendo dinheiro</em>
                        </MaskedLine>
                        <MaskedLine delay={0.51}>e você não sabe onde.</MaskedLine>
                    </h1>

                    <motion.p
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.85, ease: [0.22, 1, 0.36, 1] }}
                        className="mt-8 max-w-xl text-lg font-light leading-relaxed text-[#e6e6e6]"
                        data-testid="hero-subheadline"
                    >
                        O MasterClaus não é uma consultoria de marketing. É uma investigação
                        comercial baseada em evidências que encontra os gargalos invisíveis que
                        drenam o seu caixa e entrega o mapa exato para estancar o sangramento.
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 1.0, ease: [0.22, 1, 0.36, 1] }}
                        className="mt-10 flex flex-col items-start gap-5"
                    >
                        <ButtonGold onClick={onOpenForm} tid="hero-cta-button" data-testid="hero-cta-button">
                            <FileSearch size={15} />
                            Solicitar Esboço Investigativo Gratuito
                        </ButtonGold>
                        <p
                            className="flex items-center gap-2.5 font-mono text-[11px] uppercase tracking-[0.18em] text-[#888888]"
                            data-testid="hero-cta-subtext"
                        >
                            <ShieldCheck size={13} className="text-gold" />
                            Análise inicial baseada em dados públicos do seu negócio.
                        </p>
                    </motion.div>
                </div>

                {/* floating dossier card */}
                <motion.div
                    style={{ y: cardY, rotateX, rotateY, transformPerspective: 1000 }}
                    initial={{ opacity: 0, y: 48 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 1, delay: 0.6, ease: [0.22, 1, 0.36, 1] }}
                    className="lg:col-span-5"
                    data-testid="hero-dossier-card"
                >
                    <div className="animate-float-y">
                        <div className="relative mx-auto w-full max-w-md">
                            <div className="absolute -right-3 -top-3 h-full w-full border border-gold-faint" />
                            <div className="relative border border-line bg-[#141414]/90 p-7 shadow-[0_40px_80px_rgba(0,0,0,0.6)] backdrop-blur-md">
                                <div className="flex items-center justify-between">
                                    <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold">
                                        Dossiê Nº 001
                                    </span>
                                    <span className="-rotate-6 border border-[#b91c1c] px-2 py-1 font-mono text-[9px] font-bold uppercase tracking-[0.25em] text-[#b91c1c] opacity-80">
                                        Confidencial
                                    </span>
                                </div>
                                <div className="mt-5 h-px w-full bg-gold-faint" />
                                <div className="mt-2">
                                    {DOSSIER_ROWS.map((r) => (
                                        <div
                                            key={r.label}
                                            className="flex items-center justify-between gap-4 border-b border-white/5 py-3.5 last:border-0"
                                        >
                                            <span className="text-[13px] text-[#888888]">{r.label}</span>
                                            <span className="flex items-center gap-2 whitespace-nowrap font-mono text-[10px] tracking-[0.14em] text-white">
                                                {r.alert && (
                                                    <span className="h-1.5 w-1.5 animate-pulse-dot rounded-full bg-gold" />
                                                )}
                                                {r.value}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                                <div className="mt-5 border-t border-white/5 pt-5">
                                    <p className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#666666]">
                                        Assinatura do vazamento
                                    </p>
                                    <svg viewBox="0 0 240 56" className="mt-3 h-12 w-full" fill="none">
                                        <motion.path
                                            d="M4 10 L44 16 L84 14 L124 24 L164 22 L204 38 L236 44"
                                            stroke="#c9a227"
                                            strokeWidth="1.5"
                                            initial={{ pathLength: 0 }}
                                            animate={{ pathLength: 1 }}
                                            transition={{ duration: 1.8, delay: 1.4, ease: "easeOut" }}
                                        />
                                        <line x1="84" y1="14" x2="96" y2="40" stroke="#c9a227" strokeDasharray="2 3" opacity="0.5" />
                                        <circle cx="96" cy="45" r="2" fill="#c9a227" />
                                        <line x1="204" y1="38" x2="216" y2="50" stroke="#c9a227" strokeDasharray="2 3" opacity="0.5" />
                                        <circle cx="216" cy="53" r="2" fill="#c9a227" />
                                    </svg>
                                </div>
                                <p className="mt-4 font-mono text-[9px] leading-relaxed tracking-wider text-[#666666]">
                                    Fonte: cruzamento de evidências públicas e percepção real de clientes.
                                </p>
                            </div>
                        </div>
                    </div>
                </motion.div>
            </div>

            <motion.button
                onClick={() => scrollToSection("#problema")}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.7, duration: 0.8 }}
                className="absolute bottom-7 left-1/2 hidden -translate-x-1/2 flex-col items-center gap-3 md:flex"
                data-testid="hero-scroll-cue"
            >
                <span className="font-mono text-[10px] uppercase tracking-[0.35em] text-[#888888]">
                    Abrir o dossiê
                </span>
                <motion.span
                    animate={{ y: [0, 8, 0] }}
                    transition={{ repeat: Infinity, duration: 2.2, ease: "easeInOut" }}
                >
                    <ArrowDown size={14} className="text-gold" />
                </motion.span>
            </motion.button>
        </section>
    );
}
